/**
 * NGワードチェック処理
 * 指定した文字列に、指定した文字列リストの値が含まれているかをチェックし、
 * 含まれていた場合は、その文字列を文字列リスト(最大6件)として返します。
 * @param checkTargetString NGワードを含んでいるかどうかのチェックする対象の文字列
 * @param ngWordList NGワードのリスト
 * @returns チェックした結果のNGワードのリスト
 */
export const getNgWordList = (checkTargetString: string, ngWordList: string[]): string[] => {
  const checkResultWords: string[] = [];

  // チェック対象の文字列がブランクまたは、禁則ワードのリストが空である場合
  if (checkTargetString === '' || ngWordList.length === 0) {
    // 空を返します
    return checkResultWords;
  }
  for (const ngWord of ngWordList) {
    // 禁則文字、NGワードを互いに小文字に変換し、値を比較します
    if (toLowerCaseOnlyAlphabet(checkTargetString).includes(toLowerCaseOnlyAlphabet(ngWord))) {
      checkResultWords.push(ngWord);
    }
  }
  return checkResultWords;
};

/**
 * 改行コードを全て削除した文字列を返します
 * @param inputString
 * @returns 改行コードを削除した文字列
 */
export const getStringWithoutLineFeeds = (inputString: string): string => {
  return inputString.replace(/(\r\n|\r|\n)/g, '');
};

/**
 * ブランクを全て削除した文字列を返します
 * ブランクの定義
 * 半角スペース
 * 全角スペース
 * @param inputString
 * @returns ブランクを削除した文字列
 */
export const getStringWithoutBlank = (inputString: string): string => {
  return inputString.replace(/\s+/g, '');
};

/**
 * タブを全て削除した文字列を返します
 * @param inputString
 * @returns タブを削除した文字列
 */
export const getStringWithoutTab = (inputString: string): string => {
  return inputString.replace(/(\t)/g, '');
};

/**
 * 指定した除外対象の文字列配列の値に一致する箇所を、
 * 全て削除した文字列を返します
 * @param inputString
 * @returns タブを削除した文字列
 */
export const excludeStrings = (inputString: string, exclusions: string[]): string => {
  // チェック対象の文字列がブランクまたは、除外対象の文字列配列が空である場合
  if (inputString === '' || exclusions.length === 0) {
    // そのまま(空)を返します
    return inputString;
  }
  let output = inputString;
  for (const exclusion of exclusions) {
    const regex = new RegExp(exclusion, 'g');
    output = output.replace(regex, '');
  }
  return output;
};

/**
 * 文字列内のアルファベットを全て小文字に置換した文字列を返します
 * @param str
 * @returns アルファベットを小文字に変換した文字列
 */
export const toLowerCaseOnlyAlphabet = (str: string) => {
  return str.replace(/[A-Z]/g, (match) => String.fromCharCode(match.charCodeAt(0) + 32));
};

/**
 * 文字列からYYYY年MM月DD日を返します
 * @param date string
 * @returns
 */
export const setStringToDateFormatJapanese = (date: string): string => {
  const dateStrLength = date.length;
  if (dateStrLength < 6) return '';

  switch (dateStrLength) {
    case 6:
      return date.substring(0, 4) + '年' + date.substring(4, 6) + '月';
    case 8:
      return date.substring(0, 4) + '年' + date.substring(4, 6) + '月' + date.substring(6, 8) + '日';
    default:
      return '';
  }
};

/**
 * 単語に全角の括弧を付ける。
 * @param docOfString string
 * @returns
 */
export const setStringToBrackets = (docOfString: string): string => {
  if (docOfString.length === 0) return '';
  return `（${docOfString}）`;
};

// 3桁カンマ区切りとする.
export const setAmountComma = (amount: string) => {
  return String(amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
};

/**
 * Base64形式として文字列を置換します
 * @param prev
 * @returns 空文字,undefined,Base64HeaderJpegの場合はブランクを返却、
 */
export const addBase64Header = (prev?: string): string => {
  const Base64HeaderJpeg = 'data:image/jpeg;base64,';
  if (!prev) {
    return '';
  }
  if (Base64HeaderJpeg === prev.substring(0, Base64HeaderJpeg.length)) {
    return prev;
  } else {
    return Base64HeaderJpeg + prev;
  }
};

export const convertStringToNumber = (str: string): number => {
  const parsedNumber = Number(str);
  if (isNaN(parsedNumber)) {
    return 0;
  }
  return parsedNumber;
};

/**
 * 定数内{n}を指定したパラメータでバインドした結果の文字列を返却します \
 * ※定数のパラメータシンボル"{n}"nは0と自然数で定義してください \
 *  例 \
 *   input \
 *     baseString "{0}"は"{1}"です \
 *     args ['これ','テスト'] \
 *   return => これはテストです
 * @param baseString パラメータバインドを行うベースとなる文字列
 * @param args 可変長の文字列配列
 * @returns パラメータバインドされた文字列
 */
export const getReplacedStringByParams = (baseString: string, ...args: string[]): string => {
  let response = baseString;
  args.forEach((arg, index) => {
    response = response.replace('{' + index + '}', arg);
  });
  return response;
};
